<template>
  <v-app class="app-root">
    <!-- Frosted Glass Sticky App Bar -->
    <v-app-bar
      class="app-bar-glass px-2 px-md-4"
      elevation="0"
      density="comfortable"
    >
      <v-app-bar-nav-icon
        variant="text"
        class="mr-1"
        @click="drawer = !drawer"
      ></v-app-bar-nav-icon>

      <!-- Brand Logo -->
      <router-link
        :to="isAuthenticated ? '/homeview' : '/'"
        class="brand-link d-flex align-center text-decoration-none mr-4"
      >
        <div class="brand-icon mr-2">
          <v-icon color="white" size="20">mdi-filmstrip-box</v-icon>
        </div>
        <span class="brand-title">Media<span class="brand-title-accent">Collection</span></span>
      </router-link>

      <!-- Desktop Media Section Switcher Pills -->
      <div v-if="isAuthenticated" class="d-none d-lg-flex align-center ga-1 ml-2">
        <v-btn
          to="/homeview"
          variant="text"
          size="small"
          class="collection-nav-pill"
          :class="{ 'collection-nav-pill--active': !activeSection }"
          prepend-icon="mdi-view-grid-outline"
        >
          Hub
        </v-btn>
        <v-btn
          v-for="sec in navSectionsList"
          :key="sec.key"
          :to="`/${sec.key}/homeview`"
          variant="text"
          size="small"
          class="collection-nav-pill"
          :class="[
            `collection-nav-pill--${sec.key}`,
            { 'collection-nav-pill--active': activeSection === sec.key },
          ]"
          :prepend-icon="sec.icon"
        >
          {{ sec.title }}
        </v-btn>
      </div>

      <v-spacer></v-spacer>

      <!-- Right Action Bar -->
      <div class="d-flex align-center ga-2">
        <!-- Theme Toggle Button -->
        <v-btn
          icon
          variant="text"
          size="small"
          class="theme-toggle-btn"
          :title="isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
          @click="toggleTheme"
        >
          <v-icon :color="isDark ? 'amber' : 'primary'">
            {{ isDark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}
          </v-icon>
        </v-btn>

        <!-- Unauthenticated links -->
        <template v-if="!isAuthenticated">
          <v-btn to="/login" variant="text" class="btn-modern">Sign In</v-btn>
          <v-btn to="/register" color="primary" variant="flat" class="btn-modern btn-glow-primary">
            Get Started
          </v-btn>
        </template>

        <!-- Authenticated User Menu -->
        <template v-else>
          <!-- Current Section Context Actions (Desktop) -->
          <div v-if="sectionNav" class="d-none d-md-flex align-center ga-1 mr-1">
            <v-btn
              v-for="item in sectionNav.items.slice(1)"
              :key="item.to"
              :to="item.disabled ? undefined : item.to"
              :disabled="item.disabled"
              variant="tonal"
              size="small"
              class="section-sub-btn"
              :prepend-icon="item.icon"
            >
              {{ item.title }}
            </v-btn>
          </div>

          <!-- User Profile Dropdown -->
          <v-menu location="bottom end" transition="slide-y-transition">
            <template #activator="{ props: menuProps }">
              <v-btn
                v-bind="menuProps"
                variant="text"
                class="user-avatar-btn pl-2 pr-3 py-1"
                rounded="pill"
              >
                <v-avatar size="32" color="primary" class="mr-2 user-avatar">
                  <span class="text-caption font-weight-bold text-white">{{ userInitial }}</span>
                </v-avatar>
                <span class="text-body-2 font-weight-medium d-none d-sm-inline mr-1">
                  {{ userDisplayName }}
                </span>
                <v-icon size="18" class="text-medium-emphasis">mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-card min-width="240" class="glass-card pa-2 user-menu-card" elevation="8">
              <div class="pa-3 border-b-subtle">
                <div class="text-subtitle-2 font-weight-bold">{{ userFullName }}</div>
                <div class="text-caption text-medium-emphasis text-truncate">{{ userEmail }}</div>
                <v-chip v-if="isAdmin" size="x-small" color="primary" variant="flat" class="mt-2">
                  Admin
                </v-chip>
              </div>

              <v-list density="compact" nav class="pa-1">
                <v-list-item to="/profile" prepend-icon="mdi-account-outline" title="My Profile" />
                <v-list-item v-if="isAdmin" to="/admin" prepend-icon="mdi-shield-crown-outline" title="Admin Dashboard" />
                <v-list-item to="/about" prepend-icon="mdi-information-outline" title="About Media Collecion" />
                <v-divider class="my-1"></v-divider>
                <v-list-item
                  prepend-icon="mdi-logout-variant"
                  title="Sign Out"
                  class="text-error"
                  @click="logout"
                />
              </v-list>
            </v-card>
          </v-menu>
        </template>
      </div>
    </v-app-bar>

    <!-- Modern Sidebar Navigation Drawer -->
    <v-navigation-drawer
      v-model="drawer"
      app
      class="sidebar-glass"
      elevation="0"
    >
      <div class="pa-4 d-flex align-center justify-space-between border-b-subtle">
        <div class="d-flex align-center">
          <div class="brand-icon mr-2">
            <v-icon color="white" size="18">mdi-filmstrip-box</v-icon>
          </div>
          <div>
            <div class="brand-title text-body-1 font-weight-bold">Media<span class="brand-title-accent">Collection</span></div>
            <div class="text-caption text-medium-emphasis">Media Collection v2.0</div>
          </div>
        </div>
      </div>

      <v-list nav density="comfortable" class="pa-3">
        <v-list-item
          :to="isAuthenticated ? '/homeview' : '/'"
          prepend-icon="mdi-view-dashboard-outline"
          :title="isAuthenticated ? 'Collections Hub' : 'Home'"
          rounded="lg"
          class="mb-1"
        />

        <template v-if="isAuthenticated">
          <v-list-subheader class="text-uppercase text-caption font-weight-bold mt-3 mb-1">
            Collections
          </v-list-subheader>

          <v-list-item
            v-for="sec in navSectionsList"
            :key="sec.key"
            :to="`/${sec.key}/homeview`"
            :prepend-icon="sec.icon"
            :title="sec.title"
            rounded="lg"
            class="mb-1 drawer-collection-item"
            :class="`drawer-collection-item--${sec.key}`"
          />

          <template v-if="sectionNav">
            <v-list-subheader class="text-uppercase text-caption font-weight-bold mt-4 mb-1">
              {{ sectionNav.title }} Actions
            </v-list-subheader>
            <v-list-item
              v-for="item in sectionNav.items"
              :key="item.to"
              :to="item.disabled ? undefined : item.to"
              :disabled="item.disabled"
              :prepend-icon="item.icon"
              :title="item.title"
              rounded="lg"
              class="mb-1"
            />
          </template>

          <v-list-subheader class="text-uppercase text-caption font-weight-bold mt-4 mb-1">
            Account
          </v-list-subheader>
          <v-list-item to="/profile" prepend-icon="mdi-account-circle-outline" title="Profile Settings" rounded="lg" />
          <v-list-item v-if="isAdmin" to="/admin" prepend-icon="mdi-shield-crown-outline" title="Admin Portal" rounded="lg" />
          <v-list-item to="/about" prepend-icon="mdi-information-outline" title="About" rounded="lg" />
        </template>

        <template v-else>
          <v-list-item to="/login" prepend-icon="mdi-login" title="Sign In" rounded="lg" class="mt-2" />
          <v-list-item to="/register" prepend-icon="mdi-account-plus-outline" title="Create Account" rounded="lg" />
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- Main Content Canvas -->
    <v-main class="main-content-canvas ambient-glow-mesh">
      <v-container fluid class="pa-3 pa-sm-6 pa-lg-8 content-wrapper">
        <router-view v-slot="{ Component }">
          <transition name="page-fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { computed, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useTheme } from 'vuetify'
import { NAV_SECTIONS, getActiveSection, getSectionNav } from '@/config/navigation'

export default {
  name: 'App',
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const theme = useTheme()

    const drawer = ref(true)

    const isAuthenticated = computed(() => store.getters['auth/isAuthenticated'])
    const isAdmin = computed(() => store.getters['auth/isAdmin'])
    const user = computed(() => store.getters['auth/user'] || {})
    const activeSection = computed(() => getActiveSection(route.path))
    const sectionNav = computed(() => getSectionNav(activeSection.value))
    const navSectionsList = computed(() => Object.values(NAV_SECTIONS))

    const isDark = computed(() => theme.global.current.value.dark)

    const userDisplayName = computed(() => {
      if (user.value.firstname) return user.value.firstname
      if (user.value.name) return user.value.name.split(' ')[0]
      return 'User'
    })

    const userFullName = computed(() => {
      if (user.value.firstname && user.value.lastname) {
        return `${user.value.firstname} ${user.value.lastname}`
      }
      return user.value.name || 'My Account'
    })

    const userEmail = computed(() => user.value.email || '')

    const userInitial = computed(() => {
      const name = user.value.firstname || user.value.name || 'U'
      return name.charAt(0).toUpperCase()
    })

    const toggleTheme = () => {
      const newTheme = isDark.value ? 'light' : 'dark'
      theme.global.name.value = newTheme
      try {
        localStorage.setItem('cinevault_theme', newTheme)
      } catch (e) {
        // ignore local storage error
      }
    }

    const logout = () => {
      store.dispatch('auth/logout')
      router.push('/login')
    }

    onMounted(async () => {
      if (isAuthenticated.value && !user.value.id) {
        try {
          await store.dispatch('auth/fetchUser')
        } catch (err) {
          // ignore
        }
      }
    })

    return {
      drawer,
      isAuthenticated,
      isAdmin,
      user,
      activeSection,
      sectionNav,
      navSectionsList,
      isDark,
      userDisplayName,
      userFullName,
      userEmail,
      userInitial,
      toggleTheme,
      logout,
    }
  },
}
</script>

<style scoped>
.app-root {
  min-height: 100vh;
}

/* Glass Header */
.app-bar-glass {
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.07) !important;
  position: sticky !important;
  top: 0;
  z-index: 100;
}

.v-theme--dark .app-bar-glass {
  background: rgba(10, 14, 26, 0.85) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

/* Brand Link */
.brand-link {
  color: inherit;
  user-select: none;
}

.brand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);
}

.brand-title {
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.brand-title-accent {
  background: linear-gradient(135deg, #6366f1 0%, #ec4899 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Collection Nav Pills */
.collection-nav-pill {
  font-weight: 600 !important;
  border-radius: 9999px !important;
  opacity: 0.8;
  transition: all 0.25s ease !important;
}

.collection-nav-pill:hover {
  opacity: 1;
  background: rgba(128, 128, 128, 0.1) !important;
}

.collection-nav-pill--active {
  opacity: 1 !important;
  background: rgba(99, 102, 241, 0.12) !important;
  color: #6366f1 !important;
  font-weight: 700 !important;
}

.collection-nav-pill--books.collection-nav-pill--active {
  background: rgba(16, 185, 129, 0.12) !important;
  color: #10b981 !important;
}

.collection-nav-pill--audiobooks.collection-nav-pill--active {
  background: rgba(244, 63, 94, 0.12) !important;
  color: #f43f5e !important;
}

.collection-nav-pill--comics.collection-nav-pill--active {
  background: rgba(245, 158, 11, 0.12) !important;
  color: #f59e0b !important;
}

/* Section Sub Action Buttons */
.section-sub-btn {
  border-radius: 9999px !important;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
}

/* User Avatar & Menu */
.user-avatar-btn {
  border: 1px solid rgba(128, 128, 128, 0.15);
  transition: all 0.2s ease;
}

.user-avatar-btn:hover {
  border-color: rgba(99, 102, 241, 0.4);
  background: rgba(99, 102, 241, 0.08);
}

.user-avatar {
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
}

.border-b-subtle {
  border-bottom: 1px solid rgba(128, 128, 128, 0.12);
}

/* Sidebar Drawer */
.sidebar-glass {
  background: rgba(255, 255, 255, 0.95) !important;
  border-right: 1px solid rgba(0, 0, 0, 0.07) !important;
}

.v-theme--dark .sidebar-glass {
  background: rgba(14, 19, 34, 0.95) !important;
  border-right: 1px solid rgba(255, 255, 255, 0.07) !important;
}

.drawer-collection-item--movies :deep(.v-icon) {
  color: #6366f1;
}

.drawer-collection-item--books :deep(.v-icon) {
  color: #10b981;
}

.drawer-collection-item--audiobooks :deep(.v-icon) {
  color: #f43f5e;
}

.drawer-collection-item--comics :deep(.v-icon) {
  color: #f59e0b;
}

/* Main Canvas */
.main-content-canvas {
  min-height: calc(100vh - 64px);
  transition: background-color 0.3s ease;
}

.content-wrapper {
  max-width: 1440px;
  margin: 0 auto;
}

/* Page Transitions */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.page-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>

